import * as React from 'react';
import { AlertCircle, Check } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Shared step descriptor used by all wizard components.
 */
export interface WizardStepDef {
  label: string;
  icon?: React.ReactNode;
}

export interface WizardNavProps {
  /** Ordered list of steps (1-based in presentation, but array is plain 0-based). */
  steps: WizardStepDef[];
  /** 1-based current step (matches jackpot store convention: 1..N). */
  currentStep: number;
  /**
   * Steps whose 1-based index <= completedUpTo render with the "completed"
   * checkmark styling. No longer controls whether a step is clickable — all
   * steps are clickable so users can freely move between them.
   */
  completedUpTo: number;
  /** Invoked with a 1-based step number. */
  onStepChange: (step: number) => void;
  /** Whether the wizard is currently in expand-all mode. */
  expandAll: boolean;
  /** Toggle handler for expand-all mode. */
  onExpandAllChange: (v: boolean) => void;
  /**
   * Whether the expand-all toggle should be rendered at all.
   * When false (e.g. `allStepContent` was not provided to WizardShell),
   * the "Show all" button is hidden entirely instead of rendering as a no-op.
   * Defaults to true for backwards compatibility, but WizardShell always passes
   * the correct value.
   */
  showExpandAllToggle?: boolean;
  /**
   * 1-based step indices that currently have missing required fields. Rendered
   * with a red "missing data" indicator in the step circle. Used by the host
   * wizard to point the user at step(s) that block Save Draft / Save & Publish.
   */
  invalidSteps?: ReadonlySet<number> | number[];
}

/**
 * Left sidebar navigation list for the wizard shell.
 *
 * Behavior:
 * - Active step is highlighted.
 * - Steps with (1-based) index <= completedUpTo are clickable; the rest are muted.
 * - A "Show all" / "Single view" toggle at the bottom drives expand-all mode.
 *
 * This component is presentation-only — all state lives in the parent WizardShell.
 */
export function WizardNav({
  steps,
  currentStep,
  completedUpTo,
  onStepChange,
  expandAll,
  onExpandAllChange,
  showExpandAllToggle = true,
  invalidSteps,
}: WizardNavProps) {
  // Normalize to a Set for O(1) lookup regardless of caller's input shape.
  const invalid = React.useMemo<ReadonlySet<number>>(() => {
    if (!invalidSteps) return new Set();
    return invalidSteps instanceof Set ? invalidSteps : new Set(invalidSteps);
  }, [invalidSteps]);

  return (
    <div className="w-56 shrink-0 border-r bg-muted/30 flex flex-col py-4">
      <nav className="flex-1 space-y-1 px-2" aria-label="Wizard steps">
        {steps.map((step, idx) => {
          const stepNum = idx + 1; // 1-based
          const isActive = !expandAll && currentStep === stepNum;
          const isCompleted = stepNum <= completedUpTo;
          const isInvalid = invalid.has(stepNum);

          return (
            <button
              key={`${stepNum}-${step.label}`}
              type="button"
              onClick={() => {
                // Clicking any step exits expand-all and activates that step.
                if (expandAll) onExpandAllChange(false);
                onStepChange(stepNum);
              }}
              aria-current={isActive ? 'step' : undefined}
              aria-invalid={isInvalid || undefined}
              className={cn(
                'w-full flex items-center gap-3 px-3 py-2.5 rounded-md text-left text-sm transition-colors',
                isActive
                  ? 'bg-primary text-primary-foreground font-medium'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              <span
                className={cn(
                  'flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold shrink-0',
                  isInvalid && !isActive
                    ? 'bg-destructive/20 text-destructive'
                    : isActive
                      ? 'bg-primary-foreground text-primary'
                      : isCompleted
                        ? 'bg-primary/20 text-primary'
                        : 'bg-muted-foreground/20 text-muted-foreground',
                )}
              >
                {isInvalid && !isActive ? (
                  <AlertCircle className="h-3.5 w-3.5" />
                ) : isCompleted && !isActive ? (
                  <Check className="h-3.5 w-3.5" />
                ) : step.icon ? (
                  step.icon
                ) : (
                  stepNum
                )}
              </span>
              <span className="truncate flex-1">{step.label}</span>
              {isInvalid && (
                <span
                  className="text-[10px] font-medium uppercase tracking-wide text-destructive shrink-0"
                  title="Missing required fields"
                >
                  Missing
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Show all / Single view toggle — only rendered when expand-all content is available */}
      {showExpandAllToggle && (
        <div className="px-4 pt-3 border-t mt-2">
          <button
            type="button"
            onClick={() => onExpandAllChange(!expandAll)}
            className="text-xs text-primary hover:underline"
            aria-pressed={expandAll}
          >
            {expandAll ? 'Single view' : 'Show all'}
          </button>
        </div>
      )}
    </div>
  );
}

export default WizardNav;
